import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, Send, Eye, Edit3, Globe, Loader2, CheckCircle, XCircle,
  RefreshCw, ShoppingBag, Key, Coins, ShieldCheck, Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { id: 'es', label: 'ES' },
  { id: 'en', label: 'EN' },
  { id: 'pt', label: 'PT' },
];

const TYPE_ICONS = {
  order_confirmation: ShoppingBag,
  password_reset: Key,
  crypto_payment: Coins,
  email_verification: ShieldCheck,
  welcome_email: Heart,
};

const TYPE_COLORS = {
  transactional: 'text-green-400 bg-green-500/10 border-green-500/20',
  auth: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  onboarding: 'text-[#E8720C] bg-[#E8720C]/10 border-[#E8720C]/20',
};

const EmailControlPanel = () => {
  const [previews, setPreviews] = useState([]);
  const [lang, setLang] = useState('es');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('preview'); // preview | edit
  const [fields, setFields] = useState({});
  const [toEmail, setToEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreviews = useCallback(async (language) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/email-previews`, {
        params: { lang: language, _t: Date.now() },
        withCredentials: true,
      });
      const data = r.data.previews || r.data || [];
      const list = Array.isArray(data) ? data : [];
      setPreviews(list);
      if (list.length > 0) {
        setSelected(prev => {
          const match = prev ? list.find(p => p.id === prev.id) : null;
          return match || list[0];
        });
      }
    } catch (e) {
      console.error('Failed to load email previews', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadPreviews(lang); }, [lang, loadPreviews]);

  // Auto-populate fields when selected changes
  useEffect(() => {
    if (selected) {
      const defaultFields = {};
      (selected.fields || []).forEach(f => {
        if (f === 'name') defaultFields[f] = 'Joel Martinez';
        else if (f === 'email') defaultFields[f] = 'joel@example.com';
        else if (f === 'product') defaultFields[f] = 'Tropical Vibes Summer Beat';
        else if (f === 'license') defaultFields[f] = 'PREMIUM';
        else if (f === 'amount') defaultFields[f] = '49.99';
        else if (f === 'order_id') defaultFields[f] = 'PP-5AB1234XY567';
        else if (f === 'payment') defaultFields[f] = 'PayPal';
        else if (f === 'date') defaultFields[f] = new Date().toLocaleDateString();
        else if (f === 'crypto_type') defaultFields[f] = 'USDT (TRC-20)';
        else if (f === 'wallet_ref') defaultFields[f] = 'TXf8a9b2c3d4e5...';
        else if (f === 'reset_link') defaultFields[f] = `${API_URL}/reset-password?token=SAMPLE`;
        else if (f === 'verify_link') defaultFields[f] = `${API_URL}/verify-email/SAMPLE`;
        else defaultFields[f] = `[${f}]`;
      });
      setFields(defaultFields);
    }
  }, [selected]);

  const selectTemplate = (tmpl) => {
    setSelected(tmpl);
    setResult(null);
  };

  const getRenderedHTML = () => {
    if (!selected) return '';
    let html = selected.html || '';
    Object.entries(fields).forEach(([k, v]) => {
      const re = new RegExp(`\\{\\{${k}\\}\\}`, 'g');
      html = html.replace(re, v || '');
    });
    return html;
  };

  const getRenderedSubject = () => {
    if (!selected) return '';
    let subj = selected.subject || '';
    Object.entries(fields).forEach(([k, v]) => {
      const re = new RegExp(`\\{\\{${k}\\}\\}`, 'g');
      subj = subj.replace(re, v || '');
    });
    return subj;
  };

  const handleSend = async () => {
    if (!selected || !toEmail.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/welcome-email-test`, {
        template_id: selected.id,
        language: lang,
        to_email: toEmail.trim(),
        email: toEmail.trim(),
        name: fields.name || 'Customer',
        fields,
        sender_type: selected.sender_type || 'notification',
      }, { withCredentials: true });
      setResult({ type: 'success', message: `Email sent to ${r.data.to || r.data.email || toEmail} (${(r.data.language || lang).toUpperCase()})` });
    } catch (e) {
      setResult({ type: 'error', message: e.response?.data?.detail || 'Send failed.' });
    }
    setSending(false);
  };

  const FIELD_LABELS = {
    name: 'Customer Name',
    email: 'Customer Email',
    product: 'Product Name',
    license: 'License Type',
    amount: 'Amount ($)',
    order_id: 'Order Number',
    payment: 'Payment Method',
    date: 'Date',
    crypto_type: 'Crypto Type',
    wallet_ref: 'Wallet Reference',
    reset_link: 'Reset Link',
    verify_link: 'Verification Link',
  };

  return (
    <div data-testid="email-control-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Email Control Center</h2>
          <p className="text-sm text-white/40 mt-1">Preview, edit, and send any email template manually</p>
        </div>
        <button onClick={() => loadPreviews(lang)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-testid="email-refresh">
          <RefreshCw className="w-4 h-4 text-white/50" />
        </button>
      </div>

      {/* Language Selector */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-xs text-white/30">Language:</span>
        {LANGS.map(l => (
          <button key={l.id} onClick={() => setLang(l.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
              lang === l.id ? 'bg-[#E8720C]/15 border border-[#E8720C]/30 text-[#E8720C]' : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/60'
            }`}
            data-testid={`email-lang-${l.id}`}
          >
            <Globe className="w-3 h-3" />{l.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {/* Email Type Selector (Left Sidebar) */}
          <div className="col-span-3 space-y-1.5" data-testid="email-type-list">
            {previews.map(p => {
              const Icon = TYPE_ICONS[p.id] || Mail;
              const isActive = selected?.id === p.id;
              return (
                <button key={p.id} onClick={() => { selectTemplate(p); setMode('preview'); }}
                  className={`w-full p-3 rounded-xl text-left flex items-center gap-3 transition-all ${
                    isActive ? 'bg-[#E8720C]/10 border border-[#E8720C]/25' : 'bg-white/[0.02] border border-white/5 hover:border-white/10'
                  }`}
                  data-testid={`email-type-${p.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isActive ? 'bg-[#E8720C]/20 text-[#E8720C]' : 'bg-white/5 text-white/30'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-medium truncate ${isActive ? 'text-[#E8720C]' : 'text-white/70'}`}>{p.name}</p>
                    {p.category && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded border mt-1 inline-block ${TYPE_COLORS[p.category] || 'text-white/30 bg-white/5 border-white/10'}`}>
                        {p.category}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Main Content (Right Panel) */}
          <div className="col-span-9 space-y-4">
            {!selected ? (
              <div className="glass-card p-16 text-center text-white/30 text-sm">Select an email type to preview</div>
            ) : (
              <>
                {/* Mode Toggle */}
                <div className="flex items-center gap-2">
                  <div className="flex rounded-lg overflow-hidden border border-white/10">
                    <button onClick={() => setMode('preview')}
                      className={`px-4 py-2 text-xs font-medium flex items-center gap-1.5 ${mode === 'preview' ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50'}`}
                      data-testid="email-mode-preview">
                      <Eye className="w-3 h-3" /> Preview
                    </button>
                    <button onClick={() => setMode('edit')}
                      className={`px-4 py-2 text-xs font-medium flex items-center gap-1.5 ${mode === 'edit' ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50'}`}
                      data-testid="email-mode-edit">
                      <Edit3 className="w-3 h-3" /> Edit Fields
                    </button>
                  </div>
                  <div className="ml-auto text-[10px] text-white/25">
                    Sender: {selected.sender || 'noreply@ogmusicgroup.com'}
                  </div>
                </div>

                {/* Subject */}
                <div className="glass-card p-3">
                  <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-1">Subject</label>
                  <p className="text-sm text-white/80" data-testid="email-preview-subject">{getRenderedSubject()}</p>
                </div>

                {/* EDIT MODE — Dynamic Fields */}
                {mode === 'edit' && (
                  <div className="glass-card p-4" data-testid="email-field-editor">
                    <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-3">Template Fields</label>
                    <div className="grid grid-cols-2 gap-3">
                      {(selected.fields || []).map(f => (
                        <div key={f}>
                          <label className="block text-[10px] text-white/40 mb-1">{FIELD_LABELS[f] || f}</label>
                          <input
                            value={fields[f] || ''}
                            onChange={e => setFields(prev => ({ ...prev, [f]: e.target.value }))}
                            className="w-full input-glass text-xs"
                            data-testid={`email-field-${f}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Email Preview */}
                <div className="glass-card overflow-hidden" data-testid="email-preview-container">
                  <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-[#E8720C]" />
                    <span className="text-[10px] text-white/40">{selected.name} — {lang.toUpperCase()}</span>
                  </div>
                  <div style={{ background: '#050507', padding: '24px' }}>
                    <div
                      style={{ maxWidth: '580px', margin: '0 auto' }}
                      dangerouslySetInnerHTML={{ __html: getRenderedHTML() }}
                      data-testid="email-preview-body"
                    />
                  </div>
                </div>

                {/* Send Section */}
                <div className="glass-card p-4" data-testid="email-send-section">
                  <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-3">
                    Send This Email
                  </label>
                  <div className="flex gap-2">
                    <input value={toEmail} onChange={e => { setToEmail(e.target.value); setResult(null); }}
                      className="flex-1 input-glass text-xs" placeholder="recipient@email.com"
                      data-testid="email-send-to" />
                    <button onClick={handleSend} disabled={sending || !toEmail.trim()}
                      className="px-5 py-2 rounded-lg text-xs font-semibold bg-[#E8720C] text-white hover:bg-[#E8720C]/90 disabled:opacity-40 flex items-center gap-2 transition-all"
                      data-testid="email-send-btn">
                      {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Send Email
                    </button>
                  </div>
                  {result && (
                    <div className={`mt-3 p-3 rounded-lg text-xs flex items-center gap-2 ${
                      result.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
                    }`} data-testid="email-send-result">
                      {result.type === 'success' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {result.message}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailControlPanel;
